import { useMemo } from "react";
import { bool, number, func, object } from "prop-types";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { BreakdownTableDepths, DescriptionModes } from "@repo/utils";
import {
  useEstimateEditorSettings,
  DescriptionVariantSelector,
  useEstimateTableSettings,
  useEstimateStructure,
  useEstimateEditorStore,
  useVisibilityModeCellClassName,
  usePresetFieldNames,
  VisibilityButton,
  NumerationContainer,
  DescriptionTitleEditor,
} from "@tools";
import { WysiwygEditor } from "@components";
import Grid from "@material-ui/core/Grid";
import useStyle from "./DescriptionRow.style";
import classnames from "classnames";

export const DescriptionRow = observer(({
  element,
  onAfterChange,
  columnSettings,
  isLastGroupRow,
  tableDepth = BreakdownTableDepths.SECTION,
  staticDescription,
  allowAutoFocus,
  isSectionRow,
  showBorder,
  showHeader,
}) => {
  const { t } = useTranslation();
  const structure = useEstimateStructure();
  const { visibilityMode } = useEstimateEditorStore();
  const { allowEdition, isLibrary, isProposal, isPdfPrinterView } = useEstimateEditorSettings();
  const { sectionContainerWidth, showNumerationColumn } = useEstimateTableSettings();
  const { getFieldName } = usePresetFieldNames(isProposal);
  const className = useVisibilityModeCellClassName(columnSettings.hide);
  const classes = useStyle(sectionContainerWidth);
  
  const { searchPhrases } = structure;
  const { descriptionMode } = structure.settings;
  const { isTurnedOff, descriptions } = element;
  const { descriptionId, descriptionIndex, displayName, hide } = columnSettings;
  
  const field = `pt-desc${descriptionIndex ? descriptionIndex + 1 : ""}`;
  const description = descriptions?.get(descriptionId);
  const content = description?.content;
  
  const showVisibilityButton = useMemo(() => (
    visibilityMode && descriptionMode === DescriptionModes.ROW
  ), [visibilityMode, descriptionMode]);
  
  const handleChange = (value) => {
    if(value !== content) {
      element.setDescription(descriptionId, value)
    }
    
    onAfterChange();
  }
  
  const visibilityButton = showVisibilityButton
    ? <VisibilityButton
      size="tiny"
      inRow
      right="auto"
      hidden={hide}
      onChange={columnSettings.setHide}
      className={classnames(
        `d${tableDepth}`,
        showNumerationColumn ? classes.visibilityButtonWithNumeration : classes.visibilityButtonWithoutNumeration
      )}
    />
    : undefined;
  
  return (
    <Grid
      item
      container
      role="row"
      wrap="nowrap"
      className={classnames(
        "descriptionRowContainer",
        `d${tableDepth}`,
        isLastGroupRow && "bottom",
        classes.descriptionEditorContainer,
        !isPdfPrinterView && classes.addDragHandlePadding,
        isTurnedOff && classes.contentSemiOpaque,
        className,
      )}
    >
      <WysiwygEditor
        readOnly={!allowEdition}
        autoFocus={allowAutoFocus && !description?.length}
        noEmptyHtmlStrings
        changeOnClickAway
        placeholder={t("views.editor.desc_change")}
        onChange={handleChange}
        name="description-row"
        value={content}
        actions={
          showNumerationColumn
            ? <NumerationContainer
              element={element}
              className="ml-2-5 mr-0-5 transparent"
            >
              { visibilityButton }
            </NumerationContainer>
            : visibilityButton
        }
        direction="row"
        alignItems="flex-start"
        className={classnames(
          "descriptionRow",
          `d${tableDepth}`,
          classes.descriptionEditor,
          isPdfPrinterView ? "w-full" : classes.isSticky,
          staticDescription && !isSectionRow && classes.descriptionEditorStatic,
          showBorder && classes.border,
          !isLastGroupRow && tableDepth > BreakdownTableDepths.SECTION && classes.dashedSpacing,
          isSectionRow && "preset-sectionDesc",
          !isSectionRow && "preset-rowDesc"
        )}
        searchPhrases={searchPhrases?.map(p => p.value)}
        toolbarExtension={
          !isLibrary &&
          <DescriptionVariantSelector/>
        }
      >
        {
          showHeader && (
            allowEdition
              ? <DescriptionTitleEditor columnSettings={columnSettings} className="ml-1 font-bold text-sm w-max"/>
              : <strong className={`preset-descriptionHeader ${field} mb-1 px-1-5 text-sm`}>
                { getFieldName(field, undefined, undefined, displayName) }
              </strong>
          )
        }
      </WysiwygEditor>
    </Grid>
  );
});

DescriptionRow.propTypes = {
  element: object.isRequired,
  onAfterChange: func.isRequired,
  columnSettings: object.isRequired,
  isLastGroupRow: bool,
  tableDepth: number,
  staticDescription: bool,
  allowAutoFocus: bool,
  isSectionRow: bool,
  showBorder: bool,
  showHeader: bool,
};
